/**
 * LeadboardShell — Phase 1 chrome wrapper.
 *
 * Wraps the existing LeadBoard.js tab content with the new sidebar + topbar +
 * mobile drawer + bottom bar + ⌘K command palette. Persists theme + density
 * + collapsed state + last active tab in localStorage.
 *
 * Existing tab content is rendered as `children`. The shell does not interfere
 * with the inner content's existing fetchers/state.
 */
import { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import BottomBar from './BottomBar';
import CommandPalette from './CommandPalette';
import { isTabAllowed, defaultTabFor } from './nav';

const LS_KEY = 'kgob_leadboard_v3';

function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}
function savePrefs(p) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota errors */
  }
}

export default function LeadboardShell({
  user,
  role,
  activeTab,
  onTabChange,
  onLogout,
  notificationCounts = {},
  children,
}) {
  // Read prefs once. Subsequent updates are state-driven below.
  // Default to LIGHT theme (white background) so notifications/toasts are clearly visible.
  const [theme, setTheme] = useState(() => loadPrefs().theme || 'light');
  const [density, setDensity] = useState(() => loadPrefs().density || 'comfortable');
  const [collapsed, setCollapsed] = useState(() => !!loadPrefs().collapsed);
  const [drawer, setDrawer] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Persist prefs
  useEffect(() => {
    savePrefs({ theme, density, collapsed, tab: activeTab });
  }, [theme, density, collapsed, activeTab]);

  // ⌘K / Ctrl+K → command palette (suppressed while typing in an input)
  useEffect(() => {
    const onKey = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
        const tag = ev.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || ev.target?.isContentEditable) return;
        ev.preventDefault();
        setCmdOpen((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Role-gate the active tab; fallback if user no longer has access
  useEffect(() => {
    if (!isTabAllowed(activeTab, role)) {
      onTabChange(defaultTabFor(role));
    }
  }, [activeTab, role, onTabChange]);

  const nav = (k) => {
    onTabChange(k);
    setDrawer(false);
    setCmdOpen(false);
    // Scroll content to top on tab change
    const c = document.querySelector('.lb2 .content');
    if (c) c.scrollTop = 0;
  };

  return (
    <div className="lb2" data-theme={theme} data-density={density}>
      <div className="app">
        <Sidebar
          active={activeTab}
          onNav={nav}
          role={role}
          notificationCounts={notificationCounts}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          open={drawer}
          setOpen={setDrawer}
        />
        <div className="main">
          <Topbar
            user={user}
            role={role}
            theme={theme}
            setTheme={setTheme}
            density={density}
            setDensity={setDensity}
            onMenu={() => setDrawer(true)}
            onSearch={() => setCmdOpen(true)}
            onNav={nav}
            onLogout={onLogout}
            notificationCounts={notificationCounts}
          />
          <div className="content">
            <div className="content-inner">{children}</div>
          </div>
        </div>
        <BottomBar
          active={activeTab}
          onNav={nav}
          onMore={() => setDrawer(true)}
          notificationCounts={notificationCounts}
        />
        {cmdOpen && (
          <CommandPalette
            onClose={() => setCmdOpen(false)}
            onNav={nav}
            role={role}
          />
        )}
      </div>
    </div>
  );
}
